import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, ArrowRight } from 'lucide-react';
export function AgencySignup() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    agencyName: '',
    contactName: '',
    email: '',
    phone: '',
    volume: 'Under 10 groups',
    market: 'Destination Weddings'
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Simulate API call
    setTimeout(() => {
      setIsSubmitted(true);
    }, 500);
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };
  return <section id="agency-signup" className="py-24 bg-stone-950 text-stone-50 border-t border-stone-900" data-id="element-694">
      <div className="max-w-6xl mx-auto px-6" data-id="element-695">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-696">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-697">
            {/* Left: Copy & Benefits */}
            <div data-id="element-698">
              <p className="font-mono text-xs uppercase tracking-widest text-red-500 mb-6" data-id="element-699">
                Agency Early Access
              </p>

              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.1] mb-8" data-id="element-700">
                Stop waiting days for group quotes.
              </h2>

              <p className="text-lg text-stone-400 leading-relaxed mb-12" data-id="element-701">
                Join the network of top-producing agencies using BlockQuote to
                secure inventory and close groups on the first call.
              </p>

              <div className="space-y-8 mb-12" data-id="element-702">
                {[{
                title: 'Instant Good/Better/Best Options',
                desc: 'Generate professional, branded proposals across multiple properties in seconds.'
              }, {
                title: 'Direct Resort Pricing',
                desc: 'Access live inventory and direct rates without the wholesale markup.'
              }, {
                title: 'Automated Holds & Deadlines',
                desc: 'Never miss a deposit date. Smart holds protect your inventory automatically.'
              }].map((benefit, i) => <div key={i} className="flex items-start gap-4" data-id="element-703">
                    <div className="font-mono text-sm text-red-600 mt-1 flex-shrink-0" data-id="element-704">
                      0{i + 1}
                    </div>
                    <div data-id="element-705">
                      <h3 className="font-medium text-stone-50 mb-1" data-id="element-706">
                        {benefit.title}
                      </h3>
                      <p className="text-sm text-stone-400 leading-relaxed" data-id="element-707">
                        {benefit.desc}
                      </p>
                    </div>
                  </div>)}
              </div>

              <div className="p-6 border border-stone-800 bg-stone-900/30" data-id="element-708">
                <p className="text-sm text-stone-400 italic mb-4" data-id="element-709">
                  "We close the group while they're still excited on the first
                  call. It completely changed our win rate."
                </p>
                <p className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-710">
                  — DestaWed, #1 Agency for Moon Palace
                </p>
              </div>
            </div>

            {/* Right: Form */}
            <div className="relative" data-id="element-711">
              {/* Decorative background element */}
              <div className="absolute -inset-4 bg-gradient-to-br from-red-900/20 to-transparent blur-2xl -z-10 rounded-full" data-id="element-712"></div>

              <div className="border border-stone-800 bg-stone-900/50 p-8 md:p-10 backdrop-blur-sm" data-id="element-713">
                {isSubmitted ? <motion.div initial={{
                opacity: 0,
                scale: 0.95
              }} animate={{
                opacity: 1,
                scale: 1
              }} className="flex flex-col items-center justify-center text-center py-16" data-id="element-714">
                    <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mb-6" data-id="element-715">
                      <CheckCircle2 className="w-8 h-8 text-emerald-500" data-id="element-716" />
                    </div>
                    <h3 className="font-serif text-3xl text-stone-50 mb-4" data-id="element-717">
                      You're on the list!
                    </h3>
                    <p className="text-stone-400 max-w-sm mx-auto" data-id="element-718">
                      Thanks for requesting early access. Our team will review
                      your application and reach out within 24 hours.
                    </p>
                  </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-719">
                    <div className="space-y-2" data-id="element-720">
                      <h3 className="font-serif text-2xl text-stone-50 mb-1" data-id="element-721">
                        Request Access
                      </h3>
                      <p className="text-sm text-stone-400 mb-6" data-id="element-722">
                        Join the waitlist for the agency portal.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-723">
                      <div className="space-y-2" data-id="element-724">
                        <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-725">
                          Agency Name
                        </label>
                        <input required type="text" name="agencyName" value={formData.agencyName} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" placeholder="e.g. DestaWed" data-id="element-726" />
                      </div>
                      <div className="space-y-2" data-id="element-727">
                        <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-728">
                          Contact Name
                        </label>
                        <input required type="text" name="contactName" value={formData.contactName} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" placeholder="Jane Doe" data-id="element-729" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-730">
                      <div className="space-y-2" data-id="element-731">
                        <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-732">
                          Email Address
                        </label>
                        <input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" placeholder="jane@agency.com" data-id="element-733" />
                      </div>
                      <div className="space-y-2" data-id="element-734">
                        <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-735">
                          Phone
                        </label>
                        <input required type="tel" name="phone" value={formData.phone} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors" placeholder="(555) 123-4567" data-id="element-736" />
                      </div>
                    </div>

                    <div className="space-y-2" data-id="element-737">
                      <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-738">
                        Est. Monthly Group Volume
                      </label>
                      <select name="volume" value={formData.volume} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors appearance-none" data-id="element-739">
                        <option data-id="element-740">Under 10 groups</option>
                        <option data-id="element-741">10-25 groups</option>
                        <option data-id="element-742">25-50 groups</option>
                        <option data-id="element-743">50+ groups</option>
                      </select>
                    </div>

                    <div className="space-y-2" data-id="element-744">
                      <label className="font-mono text-xs uppercase tracking-widest text-stone-500" data-id="element-745">
                        Primary Market
                      </label>
                      <select name="market" value={formData.market} onChange={handleChange} className="w-full bg-stone-950 border border-stone-800 text-stone-50 px-4 py-3 focus:outline-none focus:border-red-600 transition-colors appearance-none" data-id="element-746">
                        <option data-id="element-747">Destination Weddings</option>
                        <option data-id="element-748">Corporate/Incentive</option>
                        <option data-id="element-749">Leisure Groups</option>
                        <option data-id="element-750">Mixed/All</option>
                      </select>
                    </div>

                    <button type="submit" className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-4 px-6 flex items-center justify-center gap-2 transition-colors mt-4" data-id="element-751">
                      Request Early Access
                      <ArrowRight className="w-4 h-4" data-id="element-752" />
                    </button>

                    <p className="text-center text-xs text-stone-500 mt-4" data-id="element-753">
                      No commitment required. We'll reach out within 24 hours.
                    </p>
                  </form>}
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}